// @flow
import React, { useEffect, useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
// Components
import OptionsSelector from '../../products/components/OptionsSelector';
import AddToCartButton from '../containers/AddToCartButton';
// Styles
import './GiftCardBuyBox.css';
// Types
import type { Product } from '../../products/types';
// Analytics
import * as Analytics from '../../common/analytics';

import type { Connector } from 'react-redux';
import { connect } from 'react-redux';
import { userLoggedInConnector } from '../../user';
import { changeLineItem, changeGiftCard, addGiftCardToCart } from '../actions';
import { giftCardSelector, itemSelectorByProductId } from "../selectors";
import validationHelpers from "../../common/validationHelpers";
import FormControlInput from "../../common/components/FormControlInput";

export type Props = {
  product: Product,
  item: any,
  handleEdit: (
    itemId: number,
    quantity: Number,
    observation: string,
    portioningSize: Number,
    fatLevel: 'low' | 'medium' | 'high',
    isVacuumSelected: 'vacuum' | null,
  ) => void,
};

registerLocale('pt-BR', ptBR);

const GiftCardBuyBox = ({ product, item, handleEdit, handleAddGiftCard, giftCard }: Props) => {

  const accountFormInitialState = {
    name: '',
    message: '',
    email: '',
    amount: product.price === '0.0' ? '' : product.price,
    sendAt: '',
  }

  const [accountForm, setAccountForm] = useState(accountFormInitialState);
  const [formErrors, setFormErrors] = useState({
    nameError: '',
    emailError: '',
    amountError: '',
    sendAtError: '',
  });

  const handleFormChange = (target: string, value: string) => {
    setAccountForm(prev => ({ ...prev, [target]: value }));
    if (accountForm[target] !== value) setFormErrors(prev => ({ ...prev, [`${target}Error`]: '' }));
  };

  const validateUserInput = () => {
    let noValidationErrors = true;

    const accountFormFieldToErrorMap = {
      amount: 'amountError',
      name: 'nameError',
      email: 'emailError',
      sendAt: 'sendAtError',
    }

    Object.entries(accountFormFieldToErrorMap).forEach(([field, errorField]) => {
      const validationFunction = validationHelpers.giftCard[field];
      const error = validationFunction(
        accountForm[field]
      );

      setFormErrors(prev => ({ ...prev, [errorField]: error }));
      if (error) noValidationErrors = false;
    })

    return noValidationErrors;
  };

  const handleGiftCard = () => {
    const noValidationErrors = validateUserInput();
    if (!noValidationErrors) return false;

    handleAddGiftCard(
      product.id,
      1,
      accountForm.name,
      accountForm.message,
      accountForm.email,
      accountForm.sendAt,
      product.price === '0.0' ? accountForm.amount : product.price,
    );

    Analytics.logLineItemAdd(
      item,
      window.location.pathname,
    );

    setAccountForm(accountFormInitialState)
      
    return true
  }

  return (
    <div className="buy-box-main gift-card-main">
      <div className="buy-box-title">
        <p className="buy-box-manufacturer">{product.manufacturer}</p>
        <h1 className="buy-box-product-name">{product.name}</h1>
      </div>
      <section className="buy-box-section">
        <OptionsSelector
          options={product.options}
          productPrice={product.price}
          productUnit={product.unitForQuantity}
          isGiftCard={true}
        />
        <div>
          {product.price === '0.0' && <div className="gift-card-form-input-container">
            <FormControlInput
            variant="rounded"
            label="Valor Customizável"
            value={accountForm.amount}
            handleChangeText={value => {
              const formatedValue = value.replace(/[^0-9]/g, '')
              handleFormChange('amount', formatedValue)
            }}
            handleBlur={() => validateUserInput()}
            handleFocus={value => handleFormChange('amount', value)}
            errorMessage={formErrors.amountError || ''}
          /> </div>}
          <div className="gift-card-message-container">
            <FormControlInput
              variant="rounded"
              label="E-mail do Destinatário"
              value={accountForm.email}
              handleChangeText={value => handleFormChange('email', value)}
              handleBlur={() => validateUserInput()}
              handleFocus={value => handleFormChange('email', value)}
              errorMessage={formErrors.emailError || ''}
            />
          </div>
          <div>
            <FormControlInput
              variant="rounded"
              label="Nome do Destinatário"
              value={accountForm.name}
              handleChangeText={value => handleFormChange('name', value)}
              handleBlur={() => validateUserInput()}
              handleFocus={value => handleFormChange('name', value)}
              errorMessage={formErrors.nameError || ''}
            />
          </div>
          <div className="gift-card-message-container">
            <label className="gift-card-message-label">
              Mensagem
            </label>
            <textarea
              className="gift-card-form-control-input-wrapper-message"
              placeholder="Mensagem para o Destinatário"
              value={accountForm.message}
              onChange={e => handleFormChange('message', e.target.value)}
            />
          </div> 
          <div className="form-control-input-wrapper">
            <label className="input-with-address-autocomplete-label">
              Data de envio 
            </label>
            <ReactDatePicker
              locale="pt-BR"
              placeholderText="Selecione data e hora"
              className="form-control-input form-control-input-rounded" 
              selected={accountForm.sendAt ? new Date(accountForm.sendAt) : null}
              onChange={(date) => {
                handleFormChange('sendAt', date ? date.toISOString() : '');
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="P p"
            />
            {!!formErrors.sendAtError && (
              <span className="form-control-input-message form-control-input-message-animation">
                {formErrors.sendAtError}
              </span>
            )}
          </div>
        </div>
        <div className="buy-box-section-price-container">
          <div className="buy-box-sub-container">
            <p className="buy-box-sub-container-title">Preço Estimado</p>
            <div className={`buy-price-single`}>
              <h3>{parseFloat(product.price === '0.0' ? accountForm.amount || '0.0' : product.price,
              ).toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}</h3>
            </div>
          </div>
          <div className="buy-box-section-price-container-action">
            <AddToCartButton
              showNotifyMeButton={true}
              productId={product.id}
              slug={product.slug}
              unitForQuantity={product.unitForQuantity}
              defaultQuantity={product.defaultQuantity}
              incrementQuantity={product.incrementQuantity}
              inStock={product.inStock}
              discontinued={product.discontinued}
              shouldNotifyUser={product.shouldNotifyUser}
              analyticsEventType={'visit'} // USE ANALYTICS
              eneableFatLevel={product.fatLevelEnabled}
              eneablePackagingName={product.vacuumPackable}
              fatLevel={item && item.fatLevel}
              packagingName={item && item.packagingName}
              portionQuantity={item?.portionQuantity}
              handleAddGiftCard={handleGiftCard}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const mapStateToProps = (state, props: Props) => {
  const item = itemSelectorByProductId(state, props.product.id);
  return {
    item: item,
    giftCard: giftCardSelector(state)
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    handleEdit: (
      itemId: number,
      quantity: Number,
      observation: string,
      portioningSize: number,
      fatLevel: 'low' | 'medium' | 'high',
      packagingName: 'vacuum' | null,
    ) => {
      dispatch(
        changeLineItem(
          itemId,
          observation,
          quantity,
          portioningSize,
          fatLevel,
          packagingName,
        ),
      );
    },
    handleAddGiftCard: (
      productId: number,
      quantity: number,
      receiverName: string,
      message: string,
      email: string,
      sendAt: string,
      amount: number,
    ) => {
      dispatch(
        addGiftCardToCart(
          productId,
          quantity,
          receiverName,
          message,
          email,
          sendAt,
          amount,
        )
      )
    },
    handleUpdateCard: (
      productId: number,
      receiverName: string,
      message: string,
      email: string,
      sendAt: string,
      amount: number,
    ) => {
      dispatch(
        changeGiftCard(
          productId,
          receiverName,
          message,
          email,
          sendAt,
          amount,
        )
      )
    }
  };
};

const connector: Connector<{}, Props> = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default userLoggedInConnector(connector(GiftCardBuyBox));
